import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Gavel, Search, Loader2, ChevronLeft, ChevronRight, AlertTriangle,
  Clock, CheckCircle2, XCircle, DollarSign, Eye, Ban, ShieldAlert,
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'text-zinc-500 bg-zinc-500/10' },
  scheduled: { label: 'Programada', color: 'text-blue-400 bg-blue-500/10' },
  live: { label: 'En Vivo', color: 'text-emerald-400 bg-emerald-500/10' },
  ending_soon: { label: 'Por Terminar', color: 'text-amber-400 bg-amber-500/10' },
  awaiting_payment: { label: 'Esperando Pago', color: 'text-orange-400 bg-orange-500/10' },
  paid: { label: 'Pagada', color: 'text-cyan-400 bg-cyan-500/10' },
  shipped: { label: 'Enviada', color: 'text-indigo-400 bg-indigo-500/10' },
  completed: { label: 'Completada', color: 'text-emerald-400 bg-emerald-500/10' },
  canceled: { label: 'Cancelada', color: 'text-red-400 bg-red-500/10' },
  fraud_review: { label: 'Fraude', color: 'text-red-500 bg-red-500/10' },
  disputed: { label: 'Disputa', color: 'text-yellow-400 bg-yellow-500/10' },
};

export default function AdminAuctionsPage() {
  const [auctions, setAuctions] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [cancelDialog, setCancelDialog] = useState(null);
  const [detailDialog, setDetailDialog] = useState(null);

  useEffect(() => { load(); }, [page, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const data = await api.adminListAuctions(params);
      setAuctions(data.auctions || []);
      setTotal(data.total || 0);
      setStats(data.stats || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      await api.adminCancelAuction(cancelDialog.id, { reason: 'admin_canceled' });
      toast.success('Subasta cancelada');
      setCancelDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (v) => v ? new Date(v).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
  const fmtMoney = (v) => v ? `$${Number(v).toLocaleString('es-MX')} MXN` : '-';

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-auctions-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Subastas</h1>
          <p className="text-xs text-zinc-600">{total} subastas en total</p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'En Vivo', value: stats.live || 0, icon: Gavel, color: 'text-emerald-400' },
          { label: 'Esperando Pago', value: stats.awaiting_payment || 0, icon: DollarSign, color: 'text-orange-400' },
          { label: 'Completadas', value: stats.completed || 0, icon: CheckCircle2, color: 'text-cyan-400' },
          { label: 'Rev. Fraude', value: stats.fraud_review || 0, icon: ShieldAlert, color: 'text-red-400' },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl border border-white/[0.04] bg-[#0A0A0D]">
            <s.icon className={`w-4 h-4 ${s.color} mb-2`} />
            <p className="text-2xl font-bold text-zinc-100">{s.value}</p>
            <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form onSubmit={e => { e.preventDefault(); setPage(1); load(); }} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <Input placeholder="Buscar subasta..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/[0.03] border-white/5 text-zinc-100 h-9 text-sm" data-testid="auctions-search" />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm" data-testid="auctions-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="live">En Vivo</SelectItem>
            <SelectItem value="ending_soon">Por Terminar</SelectItem>
            <SelectItem value="awaiting_payment">Esperando Pago</SelectItem>
            <SelectItem value="completed">Completadas</SelectItem>
            <SelectItem value="canceled">Canceladas</SelectItem>
            <SelectItem value="fraud_review">Fraude</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.04]">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Subasta</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Vendedor</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Precio Actual</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Pujas</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Status</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Cierra</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
              ) : auctions.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-600 text-sm">Sin subastas</td></tr>
              ) : auctions.map(a => {
                const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.draft;
                return (
                  <tr key={a.id} className="border-b border-white/[0.02] hover:bg-white/[0.01]" data-testid={`auction-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-zinc-200 text-xs font-medium truncate max-w-[200px]">{a.title}</p>
                      <p className="text-zinc-700 text-[10px]">{a.species_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-zinc-300 text-xs">{a.seller_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-zinc-100 text-xs font-medium">{fmtMoney(a.current_bid || a.starting_price)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-zinc-300 text-xs">{a.bid_count || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] text-zinc-600">{fmt(a.end_time)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => setDetailDialog(a)}
                          className="h-7 text-[10px] text-zinc-300 hover:bg-white/5" data-testid={`view-auction-${a.id}`}>
                          <Eye className="w-3 h-3 mr-1" /> Ver
                        </Button>
                        {!['completed', 'canceled'].includes(a.status) && (
                          <Button size="sm" variant="ghost" onClick={() => setCancelDialog(a)}
                            className="h-7 text-[10px] text-red-400 hover:bg-red-500/10" data-testid={`cancel-auction-${a.id}`}>
                            <Ban className="w-3 h-3 mr-1" /> Cancelar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
          <span className="text-xs text-zinc-600">Pagina {page} de {Math.ceil(total / 20) || 1}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" disabled={auctions.length < 20} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-lg" data-testid="auction-detail-dialog">
          <DialogHeader><DialogTitle className="text-zinc-100">{detailDialog?.title}</DialogTitle></DialogHeader>
          {detailDialog && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-[10px] text-zinc-600 block">Vendedor</span><span className="text-zinc-200">{detailDialog.seller_name}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Email</span><span className="text-zinc-200">{detailDialog.seller_email}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Precio Inicio</span><span className="text-zinc-200">{fmtMoney(detailDialog.starting_price)}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Puja Actual</span><span className="text-emerald-400 font-medium">{fmtMoney(detailDialog.current_bid)}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Pujas</span><span className="text-zinc-200">{detailDialog.bid_count || 0}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Status</span><span className={`text-[10px] px-2 py-0.5 rounded-full ${(STATUS_CONFIG[detailDialog.status] || {}).color}`}>{(STATUS_CONFIG[detailDialog.status] || {}).label}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Creada</span><span className="text-zinc-400 text-xs">{fmt(detailDialog.created_at)}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Cierra</span><span className="text-zinc-400 text-xs">{fmt(detailDialog.end_time)}</span></div>
              </div>
              {detailDialog.reserve_price && (
                <div><span className="text-[10px] text-zinc-600">Precio Reserva: </span><span className="text-amber-400 text-xs">{fmtMoney(detailDialog.reserve_price)}</span></div>
              )}
              {detailDialog.fraud_score > 0 && (
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                  <AlertTriangle className="w-4 h-4 text-red-400 inline mr-2" />
                  <span className="text-red-400 text-xs">Fraud Score: {detailDialog.fraud_score}</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-sm" data-testid="cancel-auction-dialog">
          <DialogHeader><DialogTitle className="text-zinc-100">Cancelar Subasta</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">Cancelar "{cancelDialog?.title}"? Esta accion no se puede revertir.</p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setCancelDialog(null)} className="border-white/10 text-zinc-400 h-9 text-xs">No, volver</Button>
            <Button onClick={handleCancel} className="bg-red-600 hover:bg-red-500 text-white h-9 text-xs" data-testid="confirm-cancel-auction">Cancelar Subasta</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
